import { useEffect, useRef, type RefObject } from 'react';

/**
 * LO MÍNIMO QUE UN MODAL LE DEBE AL TECLADO (auditoría 2026-09-15): de 20 modales hechos a mano,
 * 11 no cerraban con Escape, 6 tenían role="dialog" y ninguno atrapaba el foco — el Tab se iba a
 * la página de atrás, tapada por el overlay.
 *
 *   const dialogo = useDialog(onClose);
 *   <div ref={dialogo.ref} {...dialogo.props}> …panel… </div>
 *
 * Hace tres cosas mientras el modal está montado: Escape → onClose; Tab/Shift+Tab ciclan dentro
 * del panel; al abrir enfoca el primer control (o el panel) y al cerrar devuelve el foco a donde
 * estaba. `props` pone role="dialog", aria-modal y tabIndex=-1 para que el panel pueda recibirlo.
 */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useDialog<T extends HTMLElement = HTMLDivElement>(onClose?: () => void, opts: { autoFocus?: boolean } = {}): {
  ref: RefObject<T>;
  props: { role: 'dialog'; 'aria-modal': true; tabIndex: -1 };
} {
  const ref = useRef<T>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const autoFocus = opts.autoFocus ?? true;

  useEffect(() => {
    const previo = document.activeElement as HTMLElement | null;
    const panel = ref.current;
    if (autoFocus && panel) {
      const primero = panel.querySelector<HTMLElement>(FOCUSABLE);
      // Un timeout corto: algunos modales montan su contenido en el siguiente tick.
      const t = window.setTimeout(() => { (primero ?? panel).focus({ preventScroll: true }); }, 10);
      window.addEventListener('beforeunload', () => window.clearTimeout(t), { once: true });
    }
    const onKey = (e: KeyboardEvent) => {
      /**
       * SI EL PANEL NO ESTÁ EN EL DOM, ESTA TECLA NO ES MÍA.
       *
       * Este listener vive en `document` y en fase de CAPTURA, así que corre antes que el de
       * cualquier otro. Pero el hook se llama en el cuerpo del componente, y un componente puede
       * llamarlo y después NO renderizar nada (un `return null` porque todavía no llegó el dato).
       * En ese caso quedaba un guardián de Escape a nivel de toda la app para un modal que no
       * existe — y peor, hacía `stopPropagation()` ANTES de llamar al callback, así que se comía
       * la tecla del modal que la persona sí tenía abierto.
       *
       * Pasó en producción (Sentry 2026-09-16, EomAnnouncementModal): sin anuncio para mostrar, el
       * componente cortaba antes de definir su `seen`, y apretar Escape en cualquier pantalla
       * tiraba `Cannot access 'seen' before initialization` con la propagación ya cortada.
       *
       * `ref.current` es la señal exacta: se llena solo cuando el panel se renderizó de verdad.
       */
      if (!ref.current) return;
      if (e.key === 'Escape') {
        if (!onCloseRef.current) return;
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = [...ref.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (focusables.length === 0) { e.preventDefault(); ref.current.focus(); return; }
      const primero = focusables[0]!;
      const ultimo = focusables[focusables.length - 1]!;
      const activo = document.activeElement as HTMLElement | null;
      const dentro = !!activo && ref.current.contains(activo);
      if (e.shiftKey && (activo === primero || !dentro)) { e.preventDefault(); ultimo.focus(); }
      else if (!e.shiftKey && (activo === ultimo || !dentro)) { e.preventDefault(); primero.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      if (previo && typeof previo.focus === 'function' && document.contains(previo)) previo.focus({ preventScroll: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { ref, props: { role: 'dialog', 'aria-modal': true, tabIndex: -1 } };
}
